import { useRef, useState } from "react";
import type { LoaderError, LoaderResult } from "../load/types.js";
import { loadReceiptFromDataTransfer, loadReceiptFromInputFiles } from "../load/directory.js";
import { loadReceiptFromZip } from "../load/zip.js";
import { loadReceiptFromTarGz } from "../load/targz.js";
import { loadReceiptFromJson } from "../load/json-paste.js";

const ARCHIVE_PATTERN = /\.(zip|tar\.gz|tgz)$/i;

// Route an uploaded/dropped archive to the right adapter by extension.
function loadReceiptFromArchive(file: File): Promise<LoaderResult> {
  if (/\.(tar\.gz|tgz)$/i.test(file.name)) return loadReceiptFromTarGz(file);
  return loadReceiptFromZip(file);
}

interface ReceiptInputProps {
  onLoaded: (result: LoaderResult) => void;
  onClear: () => void;
  busy: boolean;
}

export function ReceiptInput({ onLoaded, onClear, busy }: ReceiptInputProps) {
  const [active, setActive] = useState(false);
  const [error, setError] = useState<LoaderError | null>(null);
  const [pasted, setPasted] = useState("");
  const directoryRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);

  function emit(result: LoaderResult) {
    if (result.error) setError(result.error);
    else setError(null);
    onLoaded(result);
  }

  async function handleDrop(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault();
    setActive(false);
    if (busy) return;
    const dt = ev.dataTransfer;
    const single = dt.files.length === 1 ? dt.files[0] : null;
    if (single && ARCHIVE_PATTERN.test(single.name)) {
      const result = await loadReceiptFromArchive(single);
      emit(result);
      return;
    }
    const result = await loadReceiptFromDataTransfer(dt);
    emit(result);
  }

  async function handleDirectoryPick(ev: React.ChangeEvent<HTMLInputElement>) {
    const files = ev.target.files;
    if (!files) return;
    const result = await loadReceiptFromInputFiles(files);
    emit(result);
  }

  async function handleArchivePick(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    if (!file) return;
    const result = await loadReceiptFromArchive(file);
    emit(result);
  }

  function handlePaste() {
    if (!pasted.trim()) {
      setError({ code: "empty_paste", message: "Paste a receipt JSON bundle before submitting." });
      return;
    }
    const result = loadReceiptFromJson(pasted);
    emit(result);
  }

  function handleClear() {
    setPasted("");
    setError(null);
    if (directoryRef.current) directoryRef.current.value = "";
    if (zipRef.current) zipRef.current.value = "";
    onClear();
  }

  return (
    <div className="input-panel">
      <div
        className={`dropzone ${active ? "active" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setActive(true);
        }}
        onDragLeave={() => setActive(false)}
        onDrop={handleDrop}
        onClick={() => directoryRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Drop a receipt directory or click to select files"
      >
        <p className="dropzone-primary">Drop a Sequesign receipt directory, ZIP, or tar.gz here.</p>
        <p className="dropzone-secondary">
          Or click to browse for the receipt files. Verification runs entirely in your browser.
        </p>
        <input
          ref={directoryRef}
          type="file"
          multiple
          // @ts-expect-error webkitdirectory is non-standard but widely supported
          webkitdirectory=""
          directory=""
          onChange={handleDirectoryPick}
        />
      </div>

      <div className="input-row">
        <button type="button" className="input-button" onClick={() => zipRef.current?.click()}>
          Upload ZIP / tar.gz
        </button>
        <input
          ref={zipRef}
          type="file"
          accept=".zip,.tar.gz,.tgz,application/zip,application/gzip"
          style={{ display: "none" }}
          onChange={handleArchivePick}
        />
        <span className="dropzone-secondary">Or paste a Sequesign receipt JSON bundle below.</span>
      </div>

      <div className="json-paste">
        <textarea
          placeholder='{ "receipt": { ... }, "actions": [ ... ], "evidence": { ... } }'
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
          spellCheck={false}
        />
        <div className="json-paste-actions">
          <button type="button" className="input-button-primary" onClick={handlePaste}>
            Verify pasted JSON
          </button>
          <button type="button" className="input-button" onClick={handleClear}>
            Clear
          </button>
        </div>
      </div>

      {error ? <div className="loader-error">{error.message}</div> : null}
    </div>
  );
}
